import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo, useState } from 'react'
import { useRef } from 'react'

import {
  createEquipment,
  generateTanningEquipmentKey,
  getAvailableEquipments,
  getEquipment,
  getEquipmentImagePresignedUrl,
  getEquipments,
  getEquipmentActivity,
  getEquipmentAssignments,
  getEquipmentAssignmentsBySite,
  getTanningEligibility,
  getEquipmentDiagnostics,
  updateEquipment,
  disableEquipment as disableEquipmentService,
  enableEquipment as enableEquipmentService,
  updateEquipmentStatus as updateEquipmentStatusService,
  resetEquipment as resetEquipmentService,
  resyncEquipments as resyncEquipmentsService,
  deleteEquipment,
  updateEquipmentMaintenanceEmail as updateEquipmentMaintenanceEmailService,
  requestMaintenance as requestMaintenanceService,
  getTanningAgentDownloadUrl,
} from '_services/tanning/equipments.service'
import { getSiteTanningMaintenanceEmail } from '_services/tanning/sites.service'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useSiteId } from '@hapana/shared/web/hooks/site.hooks'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { uploadToSignedUrl } from '@hapana/shared/web/services/upload.service'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { imageToUrl } from '@hapana/shared/utils/file.utils'
import { useMe } from '_hooks/me.hooks'

import { EQUIPMENT_STATUSES } from '_constants/equipments'

export const getEquipmentQueryKey = (siteId, params = {}) => [
  getEquipments.key,
  { siteId, ...params },
]

const setEquipmentsListQueryData = (queryClient, siteId, updater) => {
  queryClient.setQueryData(getEquipmentQueryKey(siteId), updater)
  queryClient.setQueryData(getEquipmentQueryKey(siteId, { last: true }), updater)
}

const invalidateEquipmentsListQueries = async (queryClient) => {
  await queryClient.invalidateQueries([getEquipments.key])
}

export const useListEquipments = (siteId, options = {}) => {
  const { last, siteSessionTypeId, search, ...queryOptions } = options
  return useQuery(
    [getEquipments.key, { siteId, last, siteSessionTypeId, search }],
    getEquipments.query,
    {
      enabled: !!siteId,
      staleTime: 10000,
      select: (data) => ({
        equipments: data.equipments,
      }),
      ...queryOptions,
    }
  )
}

export const useListAvailableEquipments = (siteId) => {
  return useQuery([getAvailableEquipments.key, { siteId }], getAvailableEquipments.query, {
    enabled: !!siteId,
  })
}

export const useGetEquipment = (siteId, equipmentId, options = {}) => {
  return useQuery([getEquipment.key, { equipmentId }], getEquipment.query, {
    enabled: !!siteId && !!equipmentId,
    select: (data) => ({
      equipment: data.equipment,
    }),
    ...options,
  })
}

export const useEquipmentActivity = (siteId, startDate, endDate, clientId) => {
  return useQuery(
    [getEquipmentActivity.key, { siteId, startDate, endDate, clientId }],
    getEquipmentActivity.query,
    {
      enabled: !!siteId && !!startDate && !!endDate,
      select: (data) => data?.equipmentActivity || [],
    }
  )
}

export const useGetEquipmentAssignments = (siteId, equipmentId, clientId, last, status) => {
  return useQuery(
    [getEquipmentAssignments.key, { equipmentId, last }],
    getEquipmentAssignments.query,
    {
      enabled: !!siteId && !!equipmentId,
      select: (data) => data?.equipment?.assignments || [],
    }
  )
}

export const useGetSiteEquipmentAssignments = (siteId, clientId, last, status) => {
  return useQuery(
    [getEquipmentAssignmentsBySite.key, { siteId, clientId, last, status }],
    getEquipmentAssignmentsBySite.query,
    {
      enabled: !!siteId,
      select: (data) => data?.equipment?.assignments || [],
    }
  )
}

export const useTanningEligibility = (category, clientId, siteId, options = {}) => {
  const { openToast } = useToast()
  const { t } = useTranslation('checkIn')

  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    if (errorMessage === 'Equipment regulation not found') {
      openToast({
        title: t('checkIn:equipments.eligibility.regulationNotFound'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
      })
    }
    options?.onError?.(error)
  }

  return useQuery(
    [getTanningEligibility.key, { category, clientId, siteId }],
    getTanningEligibility.query,
    {
      enabled: !!category && !!clientId && !!siteId,
      select: (data) => data?.tanningEligibility || null,
      staleTime: 0,
      cacheTime: 0,
      onError,
      ...options,
    }
  )
}

const PRESIGNED_URL_KEY = 'generateEquipmentImagePresignedUrl'
export const useCreateEquipment = (options) => {
  const { dialogState } = useDialog()
  const { openToast } = useToast()
  const { t } = useTranslation('checkIn')
  const queryClient = useQueryClient()
  const { mutate: getEquipmentImagePresignedUrl, isLoading: isGettingPresignedUrl } =
    useGetEquipmentImagePresignedUrl()
  const { data: meData } = useMe()
  const [isUploadingImage, setIsUploadingImage] = useState(false)

  const onSuccess = async (data) => {
    const siteId = data.createEquipment.siteId
    const baseItem = { ...data.createEquipment, status: EQUIPMENT_STATUSES.AVAILABLE }
    const imageExtension = dialogState.imageExtension
    const croppedImage = dialogState.croppedImage
    const image = dialogState.image

    setEquipmentsListQueryData(queryClient, siteId, (oldData) => {
      if (!oldData?.equipments) return { equipments: [baseItem] }
      const exists = oldData.equipments.some((eq) => eq.equipmentId === baseItem.equipmentId)
      const equipments = exists
        ? oldData.equipments.map((eq) => (eq.equipmentId === baseItem.equipmentId ? baseItem : eq))
        : [...oldData.equipments, baseItem]
      return { ...oldData, equipments }
    })
    openToast({
      title: t('checkIn:equipments.manageEquipments.successfullyCreated'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onSuccess?.()

    if (imageExtension) {
      getEquipmentImagePresignedUrl(
        {
          siteId,
          fileExtension: imageExtension,
          equipmentId: data.createEquipment.equipmentId,
        },
        {
          onSuccess: async (response) => {
            const signedUrlResponse = response?.[PRESIGNED_URL_KEY]
            if (!signedUrlResponse?.tempUrl) return
            setIsUploadingImage(true)
            try {
              const blob = await fetch(croppedImage).then((r) => r.blob())
              const contentType = imageExtension === 'png' ? 'image/png' : 'image/jpeg'
              const uploadResult = await uploadToSignedUrl(signedUrlResponse, blob, {
                contentType,
              })
              if (uploadResult.ok) {
                setEquipmentsListQueryData(queryClient, siteId, (oldData) => {
                  const newItem = {
                    ...data.createEquipment,
                    status: EQUIPMENT_STATUSES.AVAILABLE,
                    image: {
                      db: image,
                      medium: image,
                      thumbnail: image,
                    },
                  }
                  if (!oldData?.equipments) return { equipments: [newItem] }
                  const exists = oldData.equipments.some(
                    (eq) => eq.equipmentId === newItem.equipmentId
                  )
                  const equipments = exists
                    ? oldData.equipments.map((eq) =>
                        eq.equipmentId === newItem.equipmentId ? newItem : eq
                      )
                    : [...oldData.equipments, newItem]
                  return { ...oldData, equipments }
                })
              }
            } finally {
              setIsUploadingImage(false)
            }
          },
        }
      )
    }
  }
  const onError = (error) => {
    if (
      error?.response?.errors[0]?.message === 'Timer ID must be unique within the site' ||
      error?.response?.errors[0]?.message.includes('E11000')
    ) {
      openToast({
        title: t('checkIn:equipments.manageEquipments.duplicateTimerId'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
      })
    } else {
      const errorMessage = error?.response?.errors?.[0]?.message
      openToast({
        title: errorMessage || t('checkIn:equipments.manageEquipments.creationError'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
      })
    }
  }

  const mutation = useMutation((data) => createEquipment({ ...data }), { onSuccess, onError })
  return { ...mutation, isLoading: mutation.isLoading || isGettingPresignedUrl || isUploadingImage }
}

export const useUpdateEquipment = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('checkIn')
  const queryClient = useQueryClient()

  const onSuccess = async (data, variables) => {
    openToast({
      title: t('checkIn:equipments.manageEquipments.successfullyUpdated'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    invalidateEquipmentsListQueries(queryClient)
    if (variables?.equipmentId) {
      queryClient.invalidateQueries([getEquipment.key, { equipmentId: variables.equipmentId }])
    }
    options?.onSuccess?.()
  }
  const onError = (error) => {
    if (
      error?.response?.errors[0]?.message === 'Timer ID must be unique within the site' ||
      error?.response?.errors[0]?.message.includes('E11000')
    ) {
      openToast({
        title: t('checkIn:equipments.manageEquipments.duplicateTimerId'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
      })
    } else {
      const errorMessage = error?.response?.errors?.[0]?.message
      openToast({
        title: errorMessage || t('checkIn:equipments.manageEquipments.updateError'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
      })
    }
  }

  return useMutation((data) => updateEquipment({ ...data }), { onSuccess, onError })
}

export const useEquipmentImageUpload = (equipment, options) => {
  const { mutate, isLoading: isUploadLoading } = useGetEquipmentImagePresignedUrl()
  const queryClient = useQueryClient()
  const { data: currentSiteId } = useSiteId()
  const { data: meData } = useMe()
  const fileExtensionRef = useRef(null)

  const presignedUrlMutation = useCallback(
    (data, outerOptions) => {
      fileExtensionRef.current = data?.fileExtension || null
      const resolvedEquipmentId = equipment?.equipment?.equipmentId ?? equipment?.equipmentId
      const resolvedSiteId = equipment?.equipment?.siteId ?? equipment?.siteId ?? currentSiteId
      mutate(
        { ...data, siteId: resolvedSiteId, equipmentId: resolvedEquipmentId },
        {
          onSuccess: async (response) => {
            const signedUrlResponse = response?.generateEquipmentImagePresignedUrl
            if (!signedUrlResponse?.tempUrl) {
              outerOptions?.onError?.(new Error('Failed to get upload URL'))
              return
            }
            try {
              const contentType = data.fileExtension === 'png' ? 'image/png' : 'image/jpeg'
              const uploadResult = await uploadToSignedUrl(signedUrlResponse, data.file, {
                contentType,
              })
              if (uploadResult.ok) {
                outerOptions?.onSuccess?.(response)
              } else {
                outerOptions?.onError?.(new Error(`Image upload failed: ${uploadResult.status}`))
              }
            } catch (error) {
              outerOptions?.onError?.(error)
            }
          },
          onError: outerOptions?.onError,
        }
      )
    },
    [
      mutate,
      meData?.me?.id,
      equipment?.equipmentId,
      equipment?.equipment?.equipmentId,
      equipment?.siteId,
      equipment?.equipment?.siteId,
      currentSiteId,
    ]
  )

  const mutation = useMemo(() => {
    const equipmentId = equipment?.equipment?.equipmentId ?? equipment?.equipmentId
    const siteId = equipment?.equipment?.siteId ?? equipment?.siteId ?? currentSiteId
    return {
      mutate: presignedUrlMutation,
      isLoading: isUploadLoading,
      responseKey: 'generateEquipmentImagePresignedUrl',
      onUploadSuccess: async (data) => {
        const localImageUrl = data?.url ?? imageToUrl(data)

        setEquipmentsListQueryData(queryClient, siteId, (oldData) => {
          if (!oldData?.equipments) return oldData
          return {
            ...oldData,
            equipments: oldData.equipments.map((eq) =>
              eq.equipmentId === equipmentId
                ? {
                    ...eq,
                    image: { db: localImageUrl, medium: localImageUrl, thumbnail: localImageUrl },
                  }
                : eq
            ),
          }
        })

        queryClient.setQueryData([getEquipment.key, { equipmentId }], (oldData) => {
          if (!oldData?.equipment) return oldData
          return {
            ...oldData,
            equipment: {
              ...oldData.equipment,
              image: { db: localImageUrl, medium: localImageUrl, thumbnail: localImageUrl },
            },
          }
        })

        if (options?.onSuccess) {
          options.onSuccess({
            image: localImageUrl,
            croppedImage: data,
            fileExtension: fileExtensionRef.current,
          })
        }

        invalidateEquipmentsListQueries(queryClient)
        queryClient.invalidateQueries([getEquipment.key, { equipmentId }])
      },
    }
  }, [
    presignedUrlMutation,
    isUploadLoading,
    queryClient,
    options,
    equipment?.siteId,
    equipment?.equipmentId,
    equipment?.equipment?.equipmentId,
    equipment?.equipment?.siteId,
    currentSiteId,
  ])

  return mutation
}

export const useGetEquipmentImagePresignedUrl = (options) => {
  return useMutation(getEquipmentImagePresignedUrl, { ...options })
}

export const useGenerateTanningEquipmentKey = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('checkIn')

  const onSuccess = (data) => {
    openToast({
      title: t('checkIn:equipments.settings.keyManagement.successfullyGeneratedKey'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })
    options?.onSuccess?.(data)
  }

  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.settings.keyManagement.failedToGenerateKey'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.(error)
  }

  return useMutation(generateTanningEquipmentKey, { ...options, onSuccess, onError })
}

export const useDisableEquipment = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const onSuccess = (data, variables) => {
    openToast({
      title: t('checkIn:equipments.manageEquipments.successfullyUpdated'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    invalidateEquipmentsListQueries(queryClient)
    if (variables?.equipmentId) {
      queryClient.invalidateQueries([getEquipment.key, { equipmentId: variables.equipmentId }])
    }
    options?.onSuccess?.(data)
  }
  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.manageEquipments.updateError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error)
  }

  return useMutation(disableEquipmentService, { ...options, onSuccess, onError })
}

export const useEnableEquipment = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const onSuccess = (data, variables) => {
    openToast({
      title: t('checkIn:equipments.manageEquipments.successfullyUpdated'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    invalidateEquipmentsListQueries(queryClient)
    if (variables?.equipmentId) {
      queryClient.invalidateQueries([getEquipment.key, { equipmentId: variables.equipmentId }])
    }
    options?.onSuccess?.(data)
  }
  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.manageEquipments.updateError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error)
  }

  return useMutation(enableEquipmentService, { ...options, onSuccess, onError })
}

export const useUpdateEquipmentStatus = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const onSuccess = (data, variables) => {
    const { siteId, equipmentId, status } = variables

    if (siteId && equipmentId && status) {
      setEquipmentsListQueryData(queryClient, siteId, (oldData) => {
        if (!oldData?.equipments) return oldData
        return {
          ...oldData,
          equipments: oldData.equipments.map((eq) =>
            eq.equipmentId === equipmentId ? { ...eq, status } : eq
          ),
        }
      })

      queryClient.setQueryData([getEquipment.key, { equipmentId }], (oldData) => {
        if (!oldData?.equipment) return oldData
        return { ...oldData, equipment: { ...oldData.equipment, status } }
      })
    }

    openToast({
      title: t('checkIn:equipments.manageEquipments.successfullyUpdated'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onSuccess?.(data)
  }

  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.manageEquipments.updateError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error)
  }

  return useMutation(updateEquipmentStatusService, { ...options, onSuccess, onError })
}

export const useResyncEquipments = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const onSuccess = async (data) => {
    openToast({
      title: t('checkIn:equipments.manageEquipments.successfullyResynced'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    await invalidateEquipmentsListQueries(queryClient)
    options?.onSuccess?.(data)
  }

  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.manageEquipments.resyncError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error)
  }

  return useMutation(resyncEquipmentsService, { ...options, onSuccess, onError })
}

export const useResetEquipment = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const onSuccess = async (data) => {
    openToast({
      title: t('checkIn:equipments.manageEquipments.successfullyReset'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    await invalidateEquipmentsListQueries(queryClient)
    options?.onSuccess?.(data)
  }

  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.manageEquipments.resetError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error)
  }

  return useMutation(resetEquipmentService, { ...options, onSuccess, onError })
}

export const useDeleteEquipment = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const queryClient = useQueryClient()
  const { data: siteId } = useSiteId()
  const { filterDialogStack } = useDialog()

  const onSuccess = async (data, variables) => {
    await invalidateEquipmentsListQueries(queryClient)
    if (variables?.equipmentId) {
      await queryClient.invalidateQueries([
        getEquipment.key,
        { equipmentId: variables.equipmentId },
      ])
    }
    openToast({
      title: t('checkIn:equipments.manageEquipments.successfullyDeleted'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    filterDialogStack('title', 'checkIn')
    options?.onSuccess?.(data)
  }

  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.manageEquipments.deleteError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error)
  }

  return useMutation(deleteEquipment, { ...options, onSuccess, onError })
}

export const useUpdateEquipmentMaintenanceEmail = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const onSuccess = (data) => {
    queryClient.invalidateQueries([getSiteTanningMaintenanceEmail.key])
    openToast({
      title: t('checkIn:equipments.settings.maintenanceSupport.updateSuccess'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onSuccess?.(data)
  }

  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.settings.maintenanceSupport.updateError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error)
  }

  return useMutation(updateEquipmentMaintenanceEmailService, { ...options, onSuccess, onError })
}

export const useRequestMaintenance = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()

  const onSuccess = (data) => {
    openToast({
      title: t('checkIn:equipments.requestMaintenance.successfullyUpdated'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onSuccess?.(data)
  }

  const onError = (error) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.requestMaintenance.updateError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error)
  }

  return useMutation(requestMaintenanceService, { ...options, onSuccess, onError })
}

export const useEquipmentDiagnostics = (siteId, options = {}) => {
  return useQuery([getEquipmentDiagnostics.key, { siteId }], getEquipmentDiagnostics.query, {
    enabled: !!siteId,
    select: (data) => data?.equipmentDiagnostics || null,
    ...options,
  })
}

export const useTanningAgentDownloadUrl = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')

  return useMutation(getTanningAgentDownloadUrl, {
    onError: () => {
      openToast({
        title: t('settings.wellness.manageEquipments.downloadAgentError'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    },
    ...options,
  })
}
